// Pure helpers shared by the voice saga and components. Nothing in here
// touches the network, sip.js or the DOM, so it can be unit tested directly.

// The FreeSWITCH dialplan (cpac_to_device) only routes vehicle SIP users to a
// vehicle: a 15-digit IMEI (TERMINUS trackers) or "<tenant id>-<vehicle id>"
// (vehicle-mode tenants). The target goes straight into the INVITE URI, so
// this is a strict whitelist: no trimming, no case change, no letters, so it
// can never reach a browser ext (admin-<id>-<session>).
const DIALABLE_SIP_USER = /^(?:[0-9]{15}|[1-9][0-9]{0,8}-[1-9][0-9]{0,18})$/

export function isDialableSipUser(target) {
  return DIALABLE_SIP_USER.test(String(target == null ? '' : target))
}

// What to dial for a presence entry. A gateway that knows SIP users sends
// sip_user on every entry, null when the vehicle has none, and that answer
// stands even when an IMEI is present. Only an older gateway without the key
// falls back to the IMEI.
export function dialTarget(vehicle) {
  if (!vehicle) return undefined
  return Object.prototype.hasOwnProperty.call(vehicle, 'sip_user')
    ? vehicle.sip_user
    : vehicle.imei
}

// Presence states from the gateway: available | busy | offline.
export function isOnline(vehicle) {
  return (
    !!vehicle && (vehicle.state === 'available' || vehicle.state === 'busy')
  )
}

export function isCallableVehicle(vehicle) {
  return isOnline(vehicle) && isDialableSipUser(dialTarget(vehicle))
}

const hasValue = v => v !== undefined && v !== null && v !== ''

// Finds the gateway presence entry for a tracking vehicle.
// 1. Match on vehicle_id (CPAC vehicles.id) when both sides carry it.
// 2. Fallback for an older gateway that sends no vehicle_id: match on code,
//    but only when exactly one entry has that code. Vehicle codes can repeat,
//    and dialling the wrong vehicle is worse than showing no button.
export function findPresenceVehicle(vehicles, { vehicleId, code } = {}) {
  const list = Array.isArray(vehicles) ? vehicles : []
  let candidates = list

  if (hasValue(vehicleId)) {
    const byId = list.find(
      v => hasValue(v.vehicle_id) && String(v.vehicle_id) === String(vehicleId),
    )
    if (byId) return byId
    // An entry that has a different vehicle_id is a different vehicle, even
    // when its code matches.
    candidates = list.filter(v => !hasValue(v.vehicle_id))
  }

  if (!hasValue(code)) return null
  const byCode = candidates.filter(
    v => hasValue(v.code) && String(v.code) === String(code),
  )
  return byCode.length === 1 ? byCode[0] : null
}

// The presence list presenceVehicleById last indexed, and that index.
let indexedVehicles = null
let vehicleIndex = new Map()

// findPresenceVehicle(vehicles, { vehicleId }) for vehicle tables, which ask
// once per row on every store update: a list of thousands of rows would scan
// the presence list thousands of times. The index is built once per presence
// list (the reducer replaces the array on every update) and, like find, keeps
// the first entry of a repeated vehicle_id.
export function presenceVehicleById(vehicles, vehicleId) {
  if (!hasValue(vehicleId)) return null
  if (vehicles !== indexedVehicles) {
    const list = Array.isArray(vehicles) ? vehicles : []
    indexedVehicles = vehicles
    vehicleIndex = new Map()
    list.forEach(v => {
      if (!v || !hasValue(v.vehicle_id)) return
      const id = String(v.vehicle_id)
      if (!vehicleIndex.has(id)) vehicleIndex.set(id, v)
    })
  }
  return vehicleIndex.get(String(vehicleId)) || null
}

// The gateway sends call_hangup only to parties of the call, but the frontend
// still checks: only a hangup for *our* active call counts. Outbound calls
// carry no gateway call id, so they always rely on the SIP session state.
export function shouldHandleGatewayHangup(payload, activeCall) {
  if (!payload || !activeCall) return false
  if (!hasValue(activeCall.callId) || !hasValue(payload.call_id)) return false
  return String(payload.call_id) === String(activeCall.callId)
}

// Why a call button is disabled (null = callable).
export function callDisabledReason({ sipState, activeCall, vehicle }) {
  if (sipState !== 'registered') return 'notRegistered'
  if (activeCall) {
    // The row of the vehicle this call is with says "in call", not "you are on
    // another call" — which would blame the vehicle you are actually calling.
    if (
      vehicle &&
      activeCall.vehicleId != null &&
      String(activeCall.vehicleId) === String(vehicle.vehicle_id)
    ) {
      return 'inCall'
    }
    return 'anotherCall'
  }
  if (!vehicle || vehicle.state === 'offline') return 'vehicleOffline'
  if (vehicle.state === 'busy') return 'vehicleBusy'
  if (vehicle.state !== 'available') return 'vehicleOffline'
  return null
}

// Gateway HTTP error -> what the saga should do.
//   unauthorized: token rejected. Stop the voice session, no retry.
//   invalid:      400, a client bug. Retrying the same request cannot help.
//   unavailable:  5xx, 404, 429, timeout, network or CORS. Retry with backoff.
export function classifyGatewayError(err) {
  const status = err && err.response ? err.response.status : undefined
  if (status === 401 || status === 403) return 'unauthorized'
  if (status === 400) return 'invalid'
  return 'unavailable'
}

// The gateway rejects a bad token with HTTP 401 before the WebSocket upgrade,
// which the browser only reports as close code 1006. These application close
// codes are honoured too, in case the gateway ever closes an open socket for
// an auth reason. 1008 is not one of them: the gateway sends it only to a tab
// that lost the race for the last per-admin session slot, which must retry.
const AUTH_CLOSE_CODES = [4001, 4003, 4401, 4403]

export function isAuthCloseCode(code) {
  return AUTH_CLOSE_CODES.indexOf(code) !== -1
}

// Bounded exponential backoff with +-20% jitter: 2s, 4s, 8s ... capped at 60s.
export function backoffDelay(attempt, options = {}) {
  const base = options.base || 2000
  const max = options.max || 60000
  const random = options.random || Math.random
  const n = Math.max(1, attempt || 1)
  const raw = Math.min(max, base * Math.pow(2, n - 1))
  const jittered = raw * (0.8 + 0.4 * random())
  return Math.round(Math.min(max, jittered))
}

// Call end causes shown to the user: busy | no-answer | unavailable |
// declined | failed. null means a normal end (hang up, cancel).
function causeFromQ850(code) {
  switch (code) {
    case 16:
      return null
    case 17:
      return 'busy'
    case 18:
    case 19:
      return 'no-answer'
    case 21:
      return 'declined'
    default:
      return 'unavailable'
  }
}

// Final INVITE response -> cause. A `Reason: Q.850;cause=N` header, when
// FreeSWITCH sends one, is more precise than the status code (480 covers both
// "no answer" and "not reachable").
export function causeFromSipResponse(statusCode, reasonHeader) {
  if (statusCode === 487) return null // our own CANCEL
  const match = /Q\.850\s*;\s*cause\s*=\s*(\d+)/i.exec(reasonHeader || '')
  const q850 = match ? Number(match[1]) : 0
  if (q850 > 0 && q850 < 128) return causeFromQ850(q850)
  switch (statusCode) {
    case 486:
    case 600:
      return 'busy'
    case 408:
      return 'no-answer'
    case 404:
    case 410:
    case 480:
    case 502:
    case 503:
    case 504:
      return 'unavailable'
    case 603:
      return 'declined'
    case 487:
      return null
    default:
      return statusCode ? 'failed' : null
  }
}

// FreeSWITCH hangup cause strings, as relayed by the gateway's call_hangup.
export function causeFromGateway(cause) {
  switch (String(cause || '').toUpperCase()) {
    case '':
    case 'NORMAL_CLEARING':
    case 'ORIGINATOR_CANCEL':
    case 'LOSE_RACE':
      return null
    case 'USER_BUSY':
      return 'busy'
    case 'NO_ANSWER':
    case 'NO_USER_RESPONSE':
    case 'ALLOTTED_TIMEOUT':
      return 'no-answer'
    case 'CALL_REJECTED':
      return 'declined'
    case 'USER_NOT_REGISTERED':
    case 'SUBSCRIBER_ABSENT':
    case 'UNALLOCATED_NUMBER':
    case 'NO_ROUTE_DESTINATION':
    case 'DESTINATION_OUT_OF_ORDER':
      return 'unavailable'
    default:
      return 'failed'
  }
}

// getUserMedia failures -> denied | notFound | unavailable | null.
export function mediaErrorKind(err) {
  if (!err) return null
  switch (err.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'notFound'
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'unavailable'
    default:
      // sip.js rejects with this when navigator.mediaDevices is missing
      // (page not served over https).
      return /insecure context/i.test(err.message || '') ? 'denied' : null
  }
}

// CPAC customer accounts (m_user.type 4, exposed as profile.user_type) may
// never use voice: Laravel's /api/voip/me refuses them, so a session would
// only end in a 401.
const CUSTOMER_USER_TYPE = 4

export function canUseVoice(profile) {
  return !!profile && Number(profile.user_type) !== CUSTOMER_USER_TYPE
}

// An account that may not use voice gets no calling UI at all (vehicle
// tables keep showing plain text), rather than controls that never work.
export function isVoiceUsable(profile, voice) {
  return canUseVoice(profile) && deriveVoiceStatus(voice) !== 'notPermitted'
}

// One status for the whole voice session.
//   off | connecting | ready | inCall | retrying | sipFailed | unauthorized |
//   stopped | notPermitted (isVoiceUsable hides calling)
export function deriveVoiceStatus(voice) {
  const v = voice || {}
  if (!v.active) {
    if (v.stopReason === 'notPermitted') return 'notPermitted'
    if (v.stopReason === 'unauthorized') return 'unauthorized'
    if (v.stopReason) return 'stopped'
    return 'off'
  }
  if (v.activeCall) return 'inCall'
  if (v.sipState === 'failed') return 'sipFailed'
  if (v.sipState === 'retrying' || v.gatewayState === 'retrying') {
    return 'retrying'
  }
  if (v.sipState === 'registered' && v.gatewayState === 'connected') {
    return 'ready'
  }
  return 'connecting'
}

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0))
  const pad = n => (n < 10 ? `0${n}` : String(n))
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const seconds = s % 60
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`
}
